import React, { useEffect, useState } from "react";
import useTestQuestion from "../useTestQuestion";
import EditImg from "../../../images/SideBar/Edit";
import Loader from "../../../components/Loader";
import Button from "../../../components/Botton";
import { Toaster } from "react-hot-toast";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
} from "@mui/material";
import DeleteImg from "../../../images/SideBar/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import EditTestQuestionIndex from "./EditTestQuestion";
import { useNavigate } from "react-router-dom";
import { formatToIST } from "../../../util/formatToIST";
import { hasPermission } from "../../../utils/permissions";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/slices/authSlice";
import { getDeleteConfirmationMessage } from "../../../utils/pagePermissions";
import { sendQuestionToTelegram } from "../../../service/ApiService";
import { toast } from "react-hot-toast";
import BulkEditModal from "./BulkEditModal";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";

interface PaginationInfo {
  hasNext: boolean;
  hasPrev: boolean;
  totalPages: number;
  currentPage: number;
  totalDocs: number;
}

const TestQuestion: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const canEdit = hasPermission(user, "question", "update");
  const canAdd = hasPermission(user, "question", "create");
  const canDelete = hasPermission(user, "question", "delete");
  const canCreateUsingExcel = hasPermission(
    user,
    "question",
    "create-using-excel"
  );
  const {
    selectedTestQuestionId,
    setSelectedTestQuestionId,
    testquestionData,
    loading,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    handleAdd,
    paginationInfo,
    handlePageChange,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    handleSearch,
    // Filter related
    filters,
    setFilters,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    courses,
    languages,
    subjectName,
    standard,
    users,
    filteredCourses,
    filteredsubjects,
    handleGeneratePdf,
    // Bulk edit functionality
    selectedQuestionIds,
    setSelectedQuestionIds,
    handleSelectQuestion,
    handleSelectAll,
    clearSelection,
    handleBulkUpdate,
    bulkEditFilteredCourses,
    bulkEditFilteredSubjects,
    handleBulkEditStandardChange,
    handleBulkEditCourseChange,
  } = useTestQuestion();

  // Local state for pagination and modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testquestionToDelete, setTestQuestionToDelete] = useState<
    string | null
  >(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  // Local state to track live MCQ status for optimistic updates
  const [liveMcqStatus, setLiveMcqStatus] = useState<Record<string, boolean>>({});

  // Handle page size change from select dropdown
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    handlePageChange(1); // Reset to first page
  };

  useEffect(() => {
    console.log("PageSize changed to:", pageSize);
  }, [pageSize]);

  // Handle direct page navigation
  const handleGoToPage = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const page = parseInt(e.currentTarget.value);
      if (page >= 1 && page <= paginationInfo.totalPages) {
        handlePageChange(page);
        e.currentTarget.value = "";
      }
    }
  };

  // Modal functions
  const openDeleteModal = (id: string) => {
    setTestQuestionToDelete(id);
    setIsModalOpen(true);
  };

  // Handle sending question to Telegram
  const handleSendToTelegram = async (question: any) => {
    try {
      // Validate question data
      if (!question.questionText) {
        toast.error("Question text is required");
        return;
      }

      if (!question.options || question.options.length === 0) {
        toast.error("Question must have at least one option");
        return;
      }

      // Check if at least one option is correct
      const hasCorrectOption = question.options.some(
        (opt: any) => opt.isCorrect === true
      );
      if (!hasCorrectOption) {
        toast.error("Question must have at least one correct option");
        return;
      }

      // Show loading toast
      toast.loading("Sending question to Telegram...");

      // Call Telegram API
      await sendQuestionToTelegram({
        questionText: question.questionText,
        options: question.options,
        description: question.description || "",
      });

      toast.dismiss();
      toast.success("Question sent to Telegram successfully!");
    } catch (error: any) {
      toast.dismiss();
      const errorMessage =
        error?.response?.data?.description ||
        error?.message ||
        "Failed to send question to Telegram";
      toast.error(errorMessage);
    }
  };

  const confirmDelete = () => {
    if (testquestionToDelete) {
      handleDelete(testquestionToDelete);
      setIsModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setTestQuestionToDelete(null);
  };

  // Handle toggling Live MCQ status
  const handleToggleLiveMcq = async (questionId: string, currentStatus: boolean) => {
    const newValue = !currentStatus;

    try {
      // Optimistically update local state
      setLiveMcqStatus(prev => ({
        ...prev,
        [questionId]: newValue
      }));

      // Call API
      await ApiService({
        method: "PATCH",
        endpoint: apiPaths.updateQuestionStatus,
        data: {
          question_id: questionId,
          isLiveMcq: newValue
        }
      });

      toast.success(`Question ${newValue ? 'added to' : 'removed from'} Live MCQ`);
    } catch (error: any) {
      // Revert on error
      setLiveMcqStatus(prev => ({
        ...prev,
        [questionId]: currentStatus
      }));
      toast.error(error?.response?.data?.message || "Failed to update status");
      console.error("Error updating question status:", error);
    }
  };

  // Calculate display range
  const startEntry = (paginationInfo.currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(
    paginationInfo.currentPage * pageSize,
    paginationInfo.totalDocs
  );

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const totalPages = paginationInfo.totalPages;
    const currentPage = paginationInfo.currentPage;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      filters.standard ||
      filters.courseName ||
      filters.subject ||
      filters.level ||
      filters.languageName ||
      filters.createdBy ||
      filters.fromDate ||
      filters.toDate
    );
  };

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {isBulkEditModalOpen ? (
        <BulkEditModal
          isOpen={isBulkEditModalOpen}
          onClose={() => setIsBulkEditModalOpen(false)}
          onSubmit={(data) => {
            handleBulkUpdate(data);
            setIsBulkEditModalOpen(false);
          }}
          selectedCount={selectedQuestionIds.length}
          standards={standard}
          courses={courses}
          subjects={subjectName}
          languages={languages}
          onStandardChange={handleBulkEditStandardChange}
          onCourseChange={handleBulkEditCourseChange}
          filteredCourses={bulkEditFilteredCourses}
          filteredSubjects={bulkEditFilteredSubjects}
        />
      ) : selectedTestQuestionId && canEdit ? (
        <EditTestQuestionIndex
          testQuestionId={selectedTestQuestionId}
          setSelectedTestQuestionId={setSelectedTestQuestionId}
        />
      ) : (
        <div className="mt-4 bg-white p-4 rounded-lg shadow-md">
          {/* Header Controls */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
            {/* Left side - Info and Page Size */}
            <div className="flex items-center gap-6">
              <h2 className="text-xl font-semibold text-gray-800">
                Test Questions
              </h2>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>
            </div>

            {/* Right side - Search, Filter Toggle and Action Buttons */}
            <div className="flex gap-3 items-center">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${hasActiveFilters()
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z"
                    />
                  </svg>
                  Filters
                  {hasActiveFilters() && (
                    <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      !
                    </span>
                  )}
                </div>
              </button>x

              {/* Bulk Edit Icon */}
              {canEdit && selectedQuestionIds.length > 0 && (
                <button
                  onClick={() => setIsBulkEditModalOpen(true)}
                  className="px-3 py-2 rounded-lg text-sm font-medium  border transition-colors flex items-center gap-2"
                  title={`Edit ${selectedQuestionIds.length} selected question(s)`}
                >
                  <EditImg />
                  <span className="text-xs">({selectedQuestionIds.length})</span>
                </button>
              )}

              {/* Generate PDF Button */}
              <Button
                onClick={
                  filters.standard && filters.courseName && filters.subject
                    ? handleGeneratePdf
                    : () => { }
                }
                className={`py-2 px-4 rounded text-nowrap ${filters.standard && filters.courseName && filters.subject
                  ? ""
                  : "opacity-50 cursor-not-allowed"
                  }`}
                type="button"
              >
                Generate PDF
              </Button>

              {canAdd && (
                <Button
                  onClick={handleAdd}
                  className="py-2 px-4 rounded text-nowrap"
                  children="Add Question"
                  type="button"
                />
              )}
              {canCreateUsingExcel && (
                <Button
                  onClick={() => navigate("/add-excel")}
                  className="py-2 px-4 rounded text-nowrap"
                  children="Add from Excel"
                  type="button"
                />
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Standard Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Standard
                  </label>
                  <select
                    value={filters.standard}
                    onChange={(e) =>
                      handleFilterChange("standard", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Standards</option>
                    {standard.map((std) => (
                      <option key={std._id} value={std.standard}>
                        {std.standard}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Course/Stream Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stream
                  </label>
                  <select
                    value={filters.courseName}
                    onChange={(e) =>
                      handleFilterChange("courseName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!filters.standard}
                  >
                    <option value="">All Streams</option>
                    {filteredCourses.map((course) => (
                      <option key={course._id} value={course.courseName}>
                        {course.courseName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subject Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <select
                    value={filters.subject}
                    onChange={(e) =>
                      handleFilterChange("subject", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!filters.standard || !filters.courseName}
                  >
                    <option value="">All Subjects</option>
                    {filteredsubjects.map((subject) => (
                      <option key={subject._id} value={subject.subject}>
                        {subject.subject}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Level Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Level
                  </label>
                  <select
                    value={filters.level}
                    onChange={(e) =>
                      handleFilterChange("level", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Levels</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                {/* Language Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Language
                  </label>
                  <select
                    value={filters.languageName}
                    onChange={(e) =>
                      handleFilterChange("languageName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Languages</option>
                    {languages.map((language) => (
                      <option key={language._id} value={language.languageName}>
                        {language.languageName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Created By Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created By
                  </label>
                  <select
                    value={filters.createdBy}
                    onChange={(e) =>
                      handleFilterChange("createdBy", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Users</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* From Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      handleFilterChange("fromDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* To Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      handleFilterChange("toDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={filters.fromDate}
                  />
                </div>
              </div>

              {/* Filter Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 transition-colors"
                >
                  Clear All
                </button>
              </div>

              {/* Active Filters Display */}
              {hasActiveFilters() && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Active Filters:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(filters).map(([key, value]) => {
                      if (!value) return null;

                      let displayValue = value;
                      if (key === "createdBy") {
                        const user = users.find((u) => u._id === value);
                        displayValue = user ? user.name : value;
                      }

                      return (
                        <span
                          key={key}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {key}: {displayValue}
                          <button
                            onClick={() => handleFilterChange(key, "")}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pagination Info */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Showing {startEntry} to {endEntry} of {paginationInfo.totalDocs}{" "}
              entries
              {paginationInfo.totalDocs > 0 && (
                <span className="ml-4">
                  Page {paginationInfo.currentPage} of{" "}
                  {paginationInfo.totalPages}
                </span>
              )}
            </p>
          </div>

          {/* Custom Table */}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    <Checkbox
                      checked={selectedQuestionIds.length === testquestionData.length && testquestionData.length > 0}
                      indeterminate={selectedQuestionIds.length > 0 && selectedQuestionIds.length < testquestionData.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{ padding: 0 }}
                    />
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Sr No
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Actions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Level
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Stream
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Language
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Education
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Is Live Mcq?
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    Added By
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    Added At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {testquestionData.map((row, index) => {
                  const globalIndex =
                    (paginationInfo.currentPage - 1) * pageSize + index + 1;
                  return (
                    <tr key={row._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <Checkbox
                          checked={selectedQuestionIds.includes(row._id)}
                          onChange={() => handleSelectQuestion(row._id)}
                          sx={{ padding: 0 }}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                        {globalIndex}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex space-x-2 justify-center">
                          <button
                            onClick={() => handleSendToTelegram(row)}
                            className="text-gray-600 hover:text-blue-500 transition-colors"
                            title="Send to Telegram"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.174 1.858-.896 6.375-1.264 8.453-.152.938-.45 1.251-.74 1.283-.63.062-1.108-.416-1.718-.816-.952-.598-1.49-.97-2.414-1.554-1.08-.668-.38-1.035.236-1.633.164-.16 2.99-2.744 3.044-2.977.006-.03.012-.145-.056-.205-.068-.06-.17-.037-.244-.022-.105.02-1.78 1.135-5.024 3.33-.476.33-.907.49-1.293.482-.428-.009-1.252-.242-1.865-.44-.752-.244-1.349-.373-1.297-.789.027-.216.325-.437.895-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.118.095.151.222.167.31.017.088.038.288.021.442z" />
                            </svg>
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => handleEdit(row._id)}
                              className="text-gray-600 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              <EditImg />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => openDeleteModal(row._id)}
                              className="text-gray-600 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <DeleteImg />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-md">
                        <div className="truncate" title={row.questionText}>
                          {row.questionText}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {row.level}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {row.courseName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {row.languageName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {row.subject}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {row.standard}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        <div
                          className="inline-flex items-center cursor-pointer"
                          onClick={() => {
                            // Get current status (use local state if exists, otherwise use row data)
                            const currentStatus = liveMcqStatus[row._id] !== undefined
                              ? liveMcqStatus[row._id]
                              : row.isLiveMcq;
                            handleToggleLiveMcq(row._id, currentStatus);
                          }}
                        >
                          <div className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${(liveMcqStatus[row._id] !== undefined ? liveMcqStatus[row._id] : row.isLiveMcq)
                            ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                            <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 flex items-center justify-center ${(liveMcqStatus[row._id] !== undefined ? liveMcqStatus[row._id] : row.isLiveMcq)
                              ? 'transform translate-x-7' : 'transform translate-x-0'
                              }`}>
                              {(liveMcqStatus[row._id] !== undefined ? liveMcqStatus[row._id] : row.isLiveMcq) ? (
                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {row.createdBy?.name || "—"}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {formatToIST(row.createdAt) || "—"}
                      </td>
                    </tr>
                  );
                })}

                {/* Empty state */}
                {testquestionData.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No test questions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Custom Pagination */}
          {paginationInfo.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Go to page:</span>
                <input
                  type="number"
                  min={1}
                  max={paginationInfo.totalPages}
                  placeholder={String(paginationInfo.currentPage)}
                  onKeyPress={handleGoToPage}
                  className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-1">
                {/* Previous button */}
                <button
                  onClick={() =>
                    handlePageChange(paginationInfo.currentPage - 1)
                  }
                  disabled={!paginationInfo.hasPrev}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {/* Page numbers */}
                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === "..." ? (
                      <span className="px-3 py-2 text-sm font-medium text-gray-500">
                        ...
                      </span>
                    ) : (
                      <button
                        onClick={() => handlePageChange(page as number)}
                        className={`px-3 py-2 text-sm font-medium border ${page === paginationInfo.currentPage
                          ? "bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}

                {/* Next button */}
                <button
                  onClick={() =>
                    handlePageChange(paginationInfo.currentPage + 1)
                  }
                  disabled={!paginationInfo.hasNext}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}


      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={getDeleteConfirmationMessage("question")}
      />

      <Toaster />
    </div>
  );
};

export default TestQuestion;
